import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';
import {MaterialModule} from '../../../material.module';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {Webinar, WebinarSeries} from '../../../models/webinar.model';
import {ToastService} from '../../../services/layout/toast.service';
import {WebinarsService} from '../../../services/webinars/webinars.service';

@Component({
    selector: 'app-webinar-series-detail',
    standalone: true,
    imports: [CommonModule, MaterialModule, BreadcrumbsComponent],
    templateUrl: './webinar-series-detail.component.html',
    styleUrls: ['./webinar-series-detail.component.scss'],
    host: {class: 'webinars-m3 webinars-shell'},
})
export class WebinarSeriesDetailComponent implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService);
    private readonly webinarsService = inject(WebinarsService);
    private readonly destroy$ = new Subject<void>();

    series: WebinarSeries | null = null;
    isLoading = false;
    readonly occurrenceColumns = ['index', 'title', 'localStart', 'utcStart', 'status', 'actions'];

    get breadcrumbs(): Array<{label: string; route?: string}> {
        return [
            {label: 'Shine Webinars', route: '/webinars'},
            {label: 'Webinars', route: '/webinars'},
            {label: this.series?.title || 'Recurring series'},
        ];
    }

    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const id = params.get('seriesId');
            if (!id) {
                this.router.navigate(['/webinars']);
                return;
            }
            this.loadSeries(id);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openOccurrence(occurrence: Webinar): void {
        this.router.navigate(['/webinars', occurrence.id]);
    }

    refresh(): void {
        if (!this.series?.id) return;
        this.loadSeries(this.series.id, true);
    }

    formatDateTime(value: string | null | undefined): string {
        if (!value) return 'TBA';
        const date = new Date(value);
        if (Number.isNaN(date.valueOf())) return 'TBA';
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    }

    formatSeriesRule(series: WebinarSeries): string {
        const recurrence = series.recurrence;
        if (!recurrence) return 'No recurrence rule';
        const interval = recurrence.interval > 1 ? `Every ${recurrence.interval}` : 'Every';
        if (recurrence.frequency === 'weekly') {
            const days = (recurrence.daysOfWeek ?? []).join(', ');
            return `${interval} week${recurrence.interval > 1 ? 's' : ''}${days ? ` on ${days}` : ''}`;
        }
        if (recurrence.frequency === 'monthly') {
            return `${interval} month${recurrence.interval > 1 ? 's' : ''} from day ${recurrence.startsOn.slice(-2)}`;
        }
        return `${interval} day${recurrence.interval > 1 ? 's' : ''}`;
    }

    private loadSeries(id: string, showToast = false): void {
        this.isLoading = true;
        this.webinarsService
            .getWebinarSeries(id, {limit: 100})
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (series) => {
                    this.series = series;
                    this.isLoading = false;
                    if (showToast) {
                        this.toastService.showSuccess('Series updated', 2000);
                    }
                },
                error: (err) => {
                    console.error('Failed to load webinar series', err);
                    this.toastService.showError('Could not load recurring series', 3500);
                    this.isLoading = false;
                    this.router.navigate(['/webinars']);
                },
            });
    }
}
